import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Bed,
  Bathtub,
  DirectionsCar,
  AspectRatio,
  LocationOn,
  CheckCircle,
} from "@mui/icons-material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const iconSize = 40;

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  // Popup State for form
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  // Popup State for Image Preview
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    const fetchProject = async () => {
      try {
        const res = await axios.get(
          `https://urbancasabackendfiles.onrender.com/api/innerProjects/${id}`
        );
        setProject(res.data);
      } catch (err) {
        console.error("Error fetching project:", err);
      }
    };
    fetchProject();
  }, [id]);

  if (!project) return <Typography align="center">Loading...</Typography>;

  // Handle form changes
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Submit query to backend
  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      alert("Please fill in all required fields.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(
        "https://urbancasabackendfiles.onrender.com/api/queries",
        formData
      );
      alert("Your inquiry has been submitted successfully!");
      setOpen(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (err) {
      console.error("Error submitting query:", err);
      alert("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f2f4f7", minHeight: "100vh" }}>
      {/* Hero Banner */}
      <Box
        sx={{
          height: { xs: "80vw", sm: "60vw", md: "35vw" },
          backgroundImage: `url(${project.thumbnail})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: 3,
            px: 4,
            py: 3,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "white",
              textAlign: "center",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              letterSpacing: 2,
              textShadow: "1px 1px 8px rgba(0,0,0,0.6)",
            }}
          >
            {project.name}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Project Stats */}
        <Grid container spacing={3} justifyContent="center" data-aos="fade-up">
          {[
            {
              icon: <Bed sx={{ fontSize: iconSize }} />,
              label: "Bedrooms",
              value: project.bedrooms,
            },
            {
              icon: <Bathtub sx={{ fontSize: iconSize }} />,
              label: "Bathrooms",
              value: project.bathrooms,
            },
            {
              icon: <DirectionsCar sx={{ fontSize: iconSize }} />,
              label: "Parking",
              value: project.parking,
            },
            {
              icon: <AspectRatio sx={{ fontSize: iconSize }} />,
              label: "Area",
              value: `${project.totalArea} squares`,
            },
            {
              icon: <LocationOn sx={{ fontSize: iconSize }} />,
              label: "Location",
              value: project.location,
            },
          ].map((item, idx) => (
            <Grid item xs={6} sm={4} md={2.4} key={idx}>
              <Card
                sx={{
                  py: 3,
                  textAlign: "center",
                  boxShadow: 4,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  {item.icon}
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mt: 1 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Description Section */}
        <Box mt={8} data-aos="fade-up">
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight={600}
            fontFamily="'Playfair Display', serif"
            gutterBottom
          >
            About the Project
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "justify", px: { xs: 2, md: 8 } }}
          >
            {project.description}
          </Typography>
        </Box>

        {/* Owner's Message */}
        <Box mt={8} data-aos="fade-up">
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight={600}
            fontFamily="'Playfair Display', serif"
            gutterBottom
          >
            Owner's Message
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "justify", px: { xs: 2, md: 8 } }}
          >
            {project.ownerMessage}
          </Typography>
        </Box>

        {/* Gallery */}
        <Box mt={8} data-aos="fade-up">
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight={600}
            fontFamily="'Playfair Display', serif"
            gutterBottom
          >
            Project Gallery
          </Typography>

          <Swiper
            modules={[Navigation, Pagination]}
            navigation
            pagination={{ clickable: true }}
            spaceBetween={30}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            style={{ marginTop: 20 }}
          >
            {project.images?.map((img, idx) => (
              <SwiperSlide key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Project Img ${idx}`}
                  onClick={() => {
                    setSelectedImage(img);
                    setOpenImage(true);
                  }}
                  sx={{
                    width: "100%",
                    height: 300,
                    objectFit: "cover",
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },
                  }}
                />
              </SwiperSlide>
            ))}

            {project.videos?.map((vid, idx) => (
              <SwiperSlide key={`vid-${idx}`}>
                <video
                  controls
                  style={{ width: "100%", height: 300, borderRadius: 8 }}
                >
                  <source src={vid} type="video/mp4" />
                </video>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        {/* Inquiry Button */}
        <Box textAlign="center" mt={8} data-aos="fade-up">
          <Button
            variant="outlined"
            sx={{
              m: 2,
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: "bold",
            }}
            onClick={() => setOpen(true)}
          >
            Submit Construction Inquiry
          </Button>
        </Box>
      </Container>

      {/* Image Popup */}
      <Dialog
  open={openImage}
  onClose={() => setOpenImage(false)}
  fullScreen   // 👈 makes dialog cover whole screen
  PaperProps={{
    sx: {
      backgroundColor: "transparent", // dark bg like gallery
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  }}
>
  <img
    src={selectedImage}
    alt="Fullscreen Preview"
    style={{
      width: "80%",
      height: "80%",
      objectFit: "contain", // keep aspect ratio
    }}
  />
</Dialog>


      {/* Inquiry Popup */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: "black",
            color: "black",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.5rem",
            color: "#1976d2",
            fontFamily: "'Playfair Display', serif",
          }}
        >
          Submit Construction Inquiry
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          {[
            { label: "Name", name: "name" },
            { label: "Email", name: "email" },
            { label: "Phone", name: "phone" },
            { label: "Service", name: "service" },
          ].map((field, idx) => (
            <TextField
              key={idx}
              margin="normal"
              label={field.label}
              name={field.name}
              fullWidth
              value={formData[field.name]}
              onChange={handleChange}
              required
              InputLabelProps={{
                style: { color: "#555", fontWeight: 500 },
              }}
              inputProps={{
                style: { color: "#000" },
              }}
              placeholder={`Enter your ${field.label.toLowerCase()}`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#ccc" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2" },
                },
              }}
            />
          ))}

          <TextField
            margin="normal"
            label="Message"
            name="message"
            fullWidth
            multiline
            rows={3}
            value={formData.message}
            onChange={handleChange}
            InputLabelProps={{
              style: { color: "#000000ff", fontWeight: 500 },
            }}
            inputProps={{
              style: { color: "#000" },
            }}
            placeholder="Enter your message..."
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#ccc" },
                "&:hover fieldset": { borderColor: "#1976d2" },
                "&.Mui-focused fieldset": { borderColor: "#1976d2" },
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{
              px: 3,
              borderRadius: 2,
              backgroundColor: "#f44336",
              color: "white",
              "&:hover": { backgroundColor: "#d32f2f" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              px: 3,
              borderRadius: 2,
              backgroundColor: "#1976d2",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#125ea2" },
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetails;
